import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  getOrderDetail,
  updateOrderDriver,
} from "../../redux/actions/orderActions";
import DefaultLayout from "../../layouts/DefaultLayout";
import Loader from "../../components/Loader";
import Alert from "../../components/Alert";
import { baseURLFile } from "../../constants";
import { getListDrivers } from "../../redux/actions/driverActions";
import { toast } from "react-toastify";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

function OrderDetailScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let { id } = useParams();

  const [driverSelect, setDriverSelect] = useState("");
  const [driverSelectError, setDriverSelectError] = useState("");

  // New state variables for the driver selection modal
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [selectedDriverInModal, setSelectedDriverInModal] = useState(null);

  // New state variables for the location tracking modal
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationRefreshInterval, setLocationRefreshInterval] = useState(null);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const detailOrder = useSelector((state) => state.getDetailOrder);
  const {
    orderDetail,
    loadingOrderDetail,
    errorOrderDetail,
  } = detailOrder;

  const listDrivers = useSelector((state) => state.getListDrivers);
  const { drivers } = listDrivers;

  const orderDriverUpdate = useSelector((state) => state.updateOrderDriver);
  const {
    loadingOrderDriverUpdate,
    successOrderDriverUpdate,
  } = orderDriverUpdate;

  const redirect = "/";
  useEffect(() => {
    if (!userInfo) {
      navigate(redirect);
    } else {
      dispatch(getOrderDetail(id));
      dispatch(getListDrivers(0));
    }
  }, [navigate, userInfo, dispatch, id]);

  // Debug: Log drivers data when it changes
  useEffect(() => {
    if (drivers) {
      console.log("Drivers data:", drivers);
      console.log("Drivers with location:", drivers.filter(driver =>
        driver.location_x != null && driver.location_y != null
      ));
    }
  }, [drivers]);

  useEffect(() => {
    if (successOrderDriverUpdate) {
      dispatch(getOrderDetail(id));
      dispatch(getListDrivers(0));
    }
  }, [successOrderDriverUpdate, dispatch, id]);

  // Set up auto-refresh for location tracking
  useEffect(() => {
    if (showLocationModal) {
      // Initial data refresh
      dispatch(getOrderDetail(id));
      dispatch(getListDrivers(0));

      // Set up interval for refreshing data every 50 seconds
      const interval = setInterval(() => {
        dispatch(getOrderDetail(id));
        dispatch(getListDrivers(0));
      }, 50000);

      setLocationRefreshInterval(interval);

      // Clean up interval when modal is closed
      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [showLocationModal, id, dispatch]);

  const getExtraItems = (extras) => {
    var name = "";

    extras?.forEach((item) => {
      if (name !== "") {
        name += ", ";
      }
      name += item.extra_item?.item_name ?? "";
      if (item.extra_item?.item_price && item.extra_item?.item_price !== 0) {
        name += `(+${parseFloat(item.extra_item?.item_price).toFixed(2)}$)`;
      }
    });

    return name;
  };

  // Create a simple store icon with a store SVG
  const storeIcon = L.divIcon({
    html: `
      <div style="
        background-color: #f59e0b;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 0 10px rgba(245, 158, 11, 0.7);
      ">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="24" height="24">
          <path d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" />
        </svg>
      </div>
    `,
    className: "store-icon",
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
  });

  // Create a driver icon using the animated GIF marker
  const driverIconUrl = window.location.origin + "/assets/icons/icons-marker-driver.png";
  const driverIcon = L.divIcon({
    html: `
      <div style="
        width: 50px;
        height: 50px;
        position: relative;
      ">
        <img
          src="${driverIconUrl}"
          style="
            width: 100%;
            height: 100%;
            object-fit: contain;
            position: absolute;
            top: 0;
            left: 0;
          "
          alt="Driver"
        />
      </div>
    `,
    className: "driver-gif-icon",
    iconSize: [50, 50],
    iconAnchor: [25, 50], // Anchor at the bottom center of the image
    popupAnchor: [0, -50]
  });

  // Create a simple client icon with a person SVG
  const clientIcon = L.divIcon({
    html: `
      <div style="
        background-color: #10b981;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 0 10px rgba(16, 185, 129, 0.7);
      ">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="24" height="24">
          <path d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
        </svg>
      </div>
    `,
    className: "client-icon",
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
  });

  return (
    <DefaultLayout>
      <div className="animate-fadeIn">
        {/* Modern Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <a
                href="/dashboard"
                className="inline-flex items-center text-sm font-medium text-primary hover:text-secondary transition-colors duration-200"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  ></path>
                </svg>
                Dashboard
              </a>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <a
                  href="/orders"
                  className="ml-1 text-sm font-medium text-primary hover:text-secondary md:ml-2 transition-colors duration-200"
                >
                  Orders
                </a>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-secondary"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  ></path>
                </svg>
                <span className="ml-1 text-sm font-medium text-secondary md:ml-2">
                  Order Details
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header with background */}
        <div
          className="relative mb-8 rounded-xl overflow-hidden bg-gradient-to-r from-primary to-secondary shadow-lg"
          style={{
            backgroundImage: "url('/assets/background-auth-screen.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-secondary/80"></div>
          <div className="relative z-10 px-6 py-8 md:px-10 md:py-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Order Details
                </h1>
                <p className="text-white/80 max-w-xl">
                  View and manage order information, items, and delivery details.
                </p>
              </div>
              <div className="mt-4 md:mt-0 bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                <div className="flex items-center">
                  <div className="mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-white">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white/70 text-xs">Order ID</p>
                    <p className="text-white font-bold text-xl">#{id}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/*  */}
        {loadingOrderDetail ? (
          <Loader />
        ) : errorOrderDetail ? (
          <Alert type={"error"} message={errorOrderDetail} />
        ) : orderDetail ? (
          <>
            {orderDetail.orderItems?.length === 0 ? (
              <Alert type={"error"} message={"Order is empty"} />
            ) : (
              <>
                {/* Order Info Cards */}
                <div className="my-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Status Card */}
                  <div className="rounded-xl border border-stroke bg-whiter p-6 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs font-medium text-bodydark uppercase tracking-wider mb-2">
                          Order Status
                        </p>
                        <div className="flex items-center">
                          <span className={`w-3 h-3 rounded-full mr-2 ${
                            orderDetail.order_status === "COMPLETED"
                              ? "bg-success"
                              : orderDetail.order_status === "PENDING"
                                ? "bg-warning"
                                : orderDetail.order_status === "CANCELLED"
                                  ? "bg-danger"
                                  : "bg-primary"
                          }`}></span>
                          <span className="text-lg font-semibold text-black">
                            {orderDetail.order_status ?? "Unknown"}
                          </span>
                        </div>
                      </div>
                      <div className="p-2.5 bg-primary/10 rounded-lg text-primary">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Total Card */}
                  <div className="rounded-xl border border-stroke bg-whiter p-6 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs font-medium text-bodydark uppercase tracking-wider mb-2">
                          Total Amount
                        </p>
                        <h3 className="text-2xl font-bold text-black">
                          ${parseFloat(orderDetail.total_order || 0).toFixed(2)}
                        </h3>
                        <div className="text-xs text-bodydark mt-1">
                          Subtotal: ${parseFloat(orderDetail.subtotal || 0).toFixed(2)} + Tax (8%)
                        </div>
                      </div>
                      <div className="p-2.5 bg-success/10 rounded-lg text-success">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Restaurant Card */}
                  {orderDetail.store ? (
                    <div className="rounded-xl border border-stroke bg-whiter p-6 shadow-sm hover:shadow-md transition-all duration-300">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-xs font-medium text-bodydark uppercase tracking-wider mb-2">
                            Restaurant
                          </p>
                          <h3 className="text-lg font-bold text-black truncate max-w-[180px]">
                            {orderDetail.store?.store_name ?? "---"}
                          </h3>
                        </div>
                        <div className="p-2.5 bg-warning/10 rounded-lg text-warning">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="my-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Customer Information Card */}
                  <div className="rounded-xl border border-stroke bg-whiter p-6 shadow-sm hover:shadow-md transition-all duration-300">
                    <div className="flex justify-between items-center mb-5">
                      <h2 className="text-lg font-semibold text-black flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                        </svg>
                        Customer Information
                      </h2>
                      <div className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1.5 rounded-lg">
                        ID: {orderDetail.user?.id ?? "---"}
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* Name */}
                      <div className="flex items-center p-3.5 bg-gray-1 rounded-lg">
                        <div className="bg-primary/10 p-2.5 rounded-lg text-primary mr-3.5">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-bodydark uppercase">
                            Name
                          </div>
                          <div className="font-medium text-black">
                            {orderDetail.first_name ?? "---"}{" "}
                            {orderDetail.last_name ?? ""}
                          </div>
                        </div>
                      </div>

                      {/* Email */}
                      <div className="flex items-center p-3.5 bg-gray-1 rounded-lg">
                        <div className="bg-primary/10 p-2.5 rounded-lg text-primary mr-3.5">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-bodydark uppercase">
                            Email
                          </div>
                          <div className="font-medium text-black">
                            {orderDetail.email ?? "---"}
                          </div>
                        </div>
                      </div>

                      {/* Phone */}
                      <div className="flex items-center p-3.5 bg-gray-1 rounded-lg">
                        <div className="bg-primary/10 p-2.5 rounded-lg text-primary mr-3.5">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-bodydark uppercase">
                            Phone
                          </div>
                          <div className="font-medium text-black">
                            {orderDetail.phone ?? "---"}
                          </div>
                        </div>
                      </div>

                      {/* Delivery Type */}
                      <div className="flex items-center p-3.5 bg-gray-1 rounded-lg">
                        <div className="bg-primary/10 p-2.5 rounded-lg text-primary mr-3.5">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-bodydark uppercase">
                            Delivery Type
                          </div>
                          <div>
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                orderDetail.delivery_type === "PICKUP"
                                  ? "bg-warning/10 text-warning border border-warning/20"
                                  : "bg-primary/10 text-primary border border-primary/20"
                              }`}
                            >
                              {orderDetail.delivery_type === "PICKUP"
                                ? "Pickup"
                                : "Delivery"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Delivery Address */}
                      {orderDetail.delivery_type !== "PICKUP" && (
                        <div className="flex items-center p-3.5 bg-gray-1 rounded-lg">
                          <div className="bg-primary/10 p-2.5 rounded-lg text-primary mr-3.5">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-bodydark uppercase">
                              Delivery Address
                            </div>
                            <div className="font-medium text-black">
                              {orderDetail.address ?? "---"}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Driver Information Card */}
                  <div
                    className="rounded-xl border border-stroke bg-whiter p-6 shadow-sm hover:shadow-md transition-all duration-300"
                    id="driverinfoid"
                  >
                    <div className="flex justify-between items-center mb-5">
                      <h2 className="text-lg font-semibold text-black flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                        </svg>
                        Driver Information
                      </h2>
                      {orderDetail.delivery && (
                        <div className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1.5 rounded-lg">
                          ID: {orderDetail.delivery?.id ?? "---"}
                        </div>
                      )}
                    </div>

                    {orderDetail.delivery_type === "PICKUP" ? (
                      <div className="flex items-center p-3.5 bg-gray-1 rounded-lg">
                        <div className="bg-warning/10 p-2.5 rounded-lg text-warning mr-3.5">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-bodydark uppercase">
                            Delivery Status
                          </div>
                          <div className="font-medium text-black">
                            Customer selected pickup delivery type
                          </div>
                        </div>
                      </div>
                    ) : orderDetail.delivery ? (
                      <div className="space-y-4">
                        {/* Driver Name */}
                        <div className="flex items-center p-3.5 bg-gray-1 rounded-lg">
                          <div className="bg-primary/10 p-2.5 rounded-lg text-primary mr-3.5">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-bodydark uppercase">
                              Name
                            </div>
                            <div className="font-medium text-black">
                              {orderDetail.delivery?.first_name ?? "---"}{" "}
                              {orderDetail.delivery?.last_name ?? ""}
                            </div>
                          </div>
                        </div>

                        {/* Driver Email */}
                        <div className="flex items-center p-3.5 bg-gray-1 rounded-lg">
                          <div className="bg-primary/10 p-2.5 rounded-lg text-primary mr-3.5">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-bodydark uppercase">
                              Email
                            </div>
                            <div className="font-medium text-black">
                              {orderDetail.delivery?.email ?? "---"}
                            </div>
                          </div>
                        </div>

                        {/* Driver Phone */}
                        <div className="flex items-center p-3.5 bg-gray-1 rounded-lg">
                          <div className="bg-primary/10 p-2.5 rounded-lg text-primary mr-3.5">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-bodydark uppercase">
                              Phone
                            </div>
                            <div className="font-medium text-black">
                              {orderDetail.delivery?.phone ?? "---"}
                            </div>
                          </div>
                        </div>

                        {/* Driver Status */}
                        <div className="flex items-center p-3.5 bg-gray-1 rounded-lg">
                          <div className="bg-success/10 p-2.5 rounded-lg text-success mr-3.5">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-xs font-medium text-bodydark uppercase">
                              Status
                            </div>
                            <div>
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-success/10 text-success border border-success/20">
                                Assigned
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="mb-5">
                          <label className="flex items-center text-sm font-medium text-body mb-2">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5 text-bodydark">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                            </svg>
                            Select Driver <span className="text-danger ml-1">*</span>
                          </label>

                          <div className="flex items-center gap-3">
                            <div className="flex-1">
                              <select
                                className={`w-full px-4 py-3 rounded-lg border-2 ${
                                  driverSelectError
                                    ? "border-danger bg-danger/5 focus:border-danger"
                                    : "border-stroke focus:border-primary bg-white"
                                } outline-none focus:ring-2 ${
                                  driverSelectError ? "focus:ring-danger/20" : "focus:ring-primary/20"
                                } text-sm transition-all duration-200`}
                                value={driverSelect}
                                onChange={(v) => setDriverSelect(v.target.value)}
                              >
                                <option value={""}>Select a driver</option>
                                {drivers?.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.full_name} {item.is_online ? "🟢 (Online)" : "🔴 (Offline)"}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <button
                              onClick={() => setShowDriverModal(true)}
                              className="px-4 py-3 rounded-lg bg-primary/10 text-primary border-2 border-primary/20 hover:bg-primary/20 transition-colors duration-200 flex items-center"
                              title="View drivers on map"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                              </svg>
                            </button>
                          </div>

                          {driverSelectError && (
                            <p className="mt-1.5 text-xs text-danger flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {driverSelectError}
                            </p>
                          )}
                        </div>

                        <div className="flex justify-end mt-6 pt-4 border-t border-stroke gap-3">
                          <a
                            href="/orders"
                            className="px-5 py-2.5 rounded-lg border-2 border-stroke text-bodydark font-medium text-sm hover:bg-gray-1 transition-colors duration-200 flex items-center"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
                            </svg>
                            Back
                          </a>
                          <button
                            disabled={loadingOrderDriverUpdate}
                            onClick={() => {
                              let check = true;
                              setDriverSelectError("");

                              if (driverSelect === "") {
                                setDriverSelectError("Please select a driver");
                                check = false;
                              }

                              if (check) {
                                dispatch(updateOrderDriver(id, driverSelect));
                              } else {
                                toast.error(
                                  "Please fix the errors before continuing"
                                );
                              }
                            }}
                            className="px-5 py-2.5 rounded-lg bg-primary text-white font-medium text-sm hover:bg-primary/90 transition-colors duration-200 shadow-sm hover:shadow-md flex items-center"
                          >
                            {loadingOrderDriverUpdate ? (
                              <>
                                <svg
                                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                Assigning...
                              </>
                            ) : (
                              <>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                                </svg>
                                Assign Driver
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="my-6">
                  {/* Map Section - Button */}
                  {!["DELIVERED", "CANCELED", "FAILED"].includes(orderDetail.order_status) && (
                    <div className="flex justify-center mb-6">
                      <button
                        onClick={() => setShowLocationModal(true)}
                        className="rounded-xl border border-stroke bg-whiter px-6 py-4 shadow-sm hover:shadow-md transition-all duration-300 flex items-center gap-3"
                      >
                        <div className="p-3 bg-primary/10 rounded-lg text-primary">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <h3 className="text-lg font-semibold text-black">Location Tracking</h3>
                          <p className="text-sm text-bodydark">View real-time location of order, driver and restaurant</p>
                        </div>
                        <div className="ml-2">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-bodydark">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                          </svg>
                        </div>
                      </button>
                    </div>
                  )}

                  {/* We don't need the hidden map container anymore since we're using modals */}
                </div>

                <div className="my-10">
                  {/* Order Items Section */}
                  <div className="rounded-xl border border-stroke bg-whiter shadow-sm hover:shadow-md transition-all duration-300 mb-8 overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-primary to-secondary px-6 py-5">
                      <div className="flex justify-between items-center">
                        <h2 className="text-xl font-bold text-white flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2.5 text-white">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                          </svg>
                          Order Items
                        </h2>
                        <div className="flex items-center">
                          <div className="bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/20">
                            <span className="text-sm font-bold text-white">
                              {orderDetail.items?.length} {orderDetail.items?.length === 1 ? 'item' : 'items'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Items List */}
                    <div className="px-6 py-5">
                      <div className="space-y-5">
                        {orderDetail.items?.map((item, index) => (
                          <div
                            className="group relative bg-white rounded-xl border border-stroke overflow-hidden transition-all duration-300 hover:shadow-md hover:border-primary/30"
                            key={index}
                          >
                            {/* Item number badge */}
                            <div className="absolute top-0 left-0 bg-primary text-white text-xs font-bold px-2.5 py-1 rounded-br-lg z-10">
                              #{index + 1}
                            </div>

                            <div className="p-5">
                              <div className="flex flex-col md:flex-row md:items-center gap-5">
                                {/* Image */}
                                <div className="relative h-24 w-24 md:h-28 md:w-28 flex-shrink-0 mx-auto md:mx-0">
                                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg"></div>
                                  <img
                                    src={baseURLFile + item.product?.product_image}
                                    alt={item.product?.product_name}
                                    className="absolute inset-0 h-full w-full object-cover rounded-lg p-1 transition-transform duration-300 group-hover:scale-105"
                                  />
                                  <div className="absolute -top-2 -right-2 bg-primary text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shadow-md">
                                    {item.quantity}
                                  </div>
                                </div>

                                {/* Content */}
                                <div className="flex-1 text-center md:text-left">
                                  <h3 className="text-lg font-bold text-black mb-2 group-hover:text-primary transition-colors duration-300">
                                    {item.product?.product_name}
                                  </h3>

                                  <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-3">
                                    <div className="flex items-center bg-primary/10 px-3 py-1 rounded-full border border-primary/20">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-primary mr-1">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                                      </svg>
                                      <span className="text-sm font-medium text-primary">
                                        ${parseFloat(item.product?.product_price).toFixed(2)}/unit
                                      </span>
                                    </div>

                                    {item.extra_items && item.extra_items.length > 0 && (
                                      <div className="flex items-center bg-secondary/10 px-3 py-1 rounded-full border border-secondary/20">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-secondary mr-1">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                                        </svg>
                                        <span className="text-sm font-medium text-secondary">
                                          {getExtraItems(item.extra_items)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Price */}
                                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 p-4 rounded-lg text-center md:min-w-[140px] shadow-sm border border-primary/10">
                                  <div className="text-xs font-semibold text-bodydark uppercase tracking-wider mb-1">
                                    Subtotal
                                  </div>
                                  <div className="text-xl font-bold text-black">
                                    ${(item.quantity * parseFloat(item.product?.product_price)).toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Summary Bar */}
                    <div className="bg-gradient-to-r from-primary/10 to-secondary/10 px-6 py-4 border-t border-stroke shadow-inner">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex items-center">
                          <div className="bg-white p-2 rounded-lg shadow-sm mr-3">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-primary">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6Z" />
                            </svg>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-bodydark uppercase">Total Items</span>
                            <p className="text-base font-bold text-black">
                              {orderDetail.items?.length} {orderDetail.items?.length === 1 ? 'item' : 'items'}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <div className="bg-white p-2 rounded-lg shadow-sm mr-3">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-blue-500">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6Z" />
                            </svg>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-bodydark uppercase">Subtotal</span>
                            <p className="text-base font-bold text-black">
                              ${parseFloat(orderDetail.subtotal || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center">
                          <div className="bg-white p-2 rounded-lg shadow-sm mr-3">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-green-500">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                            </svg>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-bodydark uppercase">Order Total</span>
                            <p className="text-base font-bold text-black">
                              ${parseFloat(orderDetail.total_order || 0).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="my-8">
                  <div className="rounded-xl border border-stroke bg-whiter p-6 shadow-sm hover:shadow-md transition-all duration-300 mb-4">
                    <h3 className="text-xl font-semibold text-black flex items-center mb-6">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 text-primary">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
                      </svg>
                      Order Summary
                    </h3>

                    <div className="space-y-5">
                      <div className="flex items-center p-3.5 bg-gray-1 rounded-lg">
                        <div className="bg-primary/10 p-2.5 rounded-lg text-primary mr-3.5">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z" />
                          </svg>
                        </div>
                        <div>
                          <div className="text-xs font-medium text-bodydark uppercase">
                            Order Date
                          </div>
                          <div className="font-medium text-black">
                            {orderDetail.created_at}
                          </div>
                        </div>
                      </div>

                      {/* Payment Method */}
                      <div className="flex items-center p-3.5 bg-gray-1 rounded-lg">
                        <div className="bg-primary/10 p-2.5 rounded-lg text-primary mr-3.5">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-xs font-medium text-bodydark uppercase">
                            Payment Method
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                orderDetail.payment_method === "CARD"
                                  ? "bg-blue-100 text-blue-700 border border-blue-200"
                                  : orderDetail.payment_method === "CASH ON DELIVERY"
                                  ? "bg-green-100 text-green-700 border border-green-200"
                                  : orderDetail.payment_method === "CALL_STORE"
                                  ? "bg-orange-100 text-orange-700 border border-orange-200"
                                  : "bg-gray-100 text-gray-700 border border-gray-200"
                              }`}
                            >
                              {orderDetail.payment_method === "CARD" && (
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 mr-1">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
                                </svg>
                              )}
                              {orderDetail.payment_method === "CASH ON DELIVERY" && (
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 mr-1">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
                                </svg>
                              )}
                              {orderDetail.payment_method === "CALL_STORE" && (
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 mr-1">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                                </svg>
                              )}
                              {orderDetail.payment_method === "CARD" ? "Credit Card" :
                               orderDetail.payment_method === "CASH ON DELIVERY" ? "Cash on Delivery" :
                               orderDetail.payment_method === "CALL_STORE" ? "Pay By Card" :
                               orderDetail.payment_method || "Not specified"}
                            </span>
                            {orderDetail.payment_method === "CALL_STORE" && orderDetail.payment_status && (
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                  orderDetail.payment_status === "PAID"
                                    ? "bg-green-100 text-green-700 border border-green-200"
                                    : "bg-red-100 text-red-700 border border-red-200"
                                }`}
                              >
                                {orderDetail.payment_status === "PAID" ? "✓ Paid" : "⏳ Unpaid"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                          <div className="text-xs font-medium text-bodydark uppercase mb-1.5">
                            Subtotal
                          </div>
                          <div className="font-semibold text-black text-lg">
                            ${parseFloat(orderDetail.subtotal || 0).toFixed(2)}
                          </div>
                        </div>

                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-bodydark uppercase mb-1.5">
                            TAX (8%)
                          </div>
                          <div className="font-semibold text-black text-lg">
                            ${parseFloat(orderDetail.tva_amount || 0).toFixed(2)}
                          </div>
                        </div>

                        <div className="p-4 bg-secondary/5 rounded-lg border border-secondary/10">
                          <div className="text-xs font-medium text-bodydark uppercase mb-1.5">
                            Delivery Fee
                          </div>
                          <div className="font-semibold text-black text-lg">
                            {orderDetail.delivery_fee
                              ? "$" + parseFloat(orderDetail.delivery_fee).toFixed(2)
                              : "Free"}
                          </div>
                        </div>

                        <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                          <div className="text-xs font-medium text-bodydark uppercase mb-1.5">
                            Total
                          </div>
                          <div className="font-semibold text-black text-lg">
                            ${parseFloat(orderDetail.total_order || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>

                      <div className="mt-5 bg-gradient-to-r from-primary to-secondary rounded-lg p-5 shadow-sm">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="text-xs font-medium text-white uppercase mb-1.5">
                              Grand Total
                            </div>
                            <div className="text-2xl font-bold text-white">
                              ${parseFloat(orderDetail.total_order || 0).toFixed(2)}
                            </div>
                            <div className="text-xs text-white/80 mt-1">
                              Subtotal: ${parseFloat(orderDetail.subtotal || 0).toFixed(2)} +
                              Tax: ${parseFloat(orderDetail.tva_amount || 0).toFixed(2)} +
                              Delivery: ${parseFloat(orderDetail.delivery_fee || 0).toFixed(2)}
                              {orderDetail.tip_amount > 0 && (
                                <> + Tip: ${parseFloat(orderDetail.tip_amount || 0).toFixed(2)}</>
                              )}
                            </div>
                          </div>
                          <div className="bg-white/10 p-3 rounded-full text-white border border-white/20 backdrop-blur-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tip Information Section */}
                {orderDetail.tip_amount > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-stroke p-6 mb-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-yellow-100 p-2 rounded-lg mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-yellow-600">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-black">Tip Information</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                        <div className="text-xs font-medium text-bodydark uppercase mb-1.5">
                          Tip Amount
                        </div>
                        <div className="font-semibold text-green-600 text-xl">
                          ${parseFloat(orderDetail.tip_amount || 0).toFixed(2)}
                        </div>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <div className="text-xs font-medium text-bodydark uppercase mb-1.5">
                          Payment Method
                        </div>
                        <div className="font-semibold text-blue-600 text-base">
                          {orderDetail.tip_payment_method === 'credit_card' ? 'Credit Card' :
                           orderDetail.tip_payment_method === 'cash' ? 'Cash' : 'None'}
                        </div>
                      </div>

                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                        <div className="text-xs font-medium text-bodydark uppercase mb-1.5">
                          Entered By
                        </div>
                        <div className="font-semibold text-purple-600 text-base">
                          {orderDetail.tip_entered_by === 'customer' ? 'Customer' :
                           orderDetail.tip_entered_by === 'driver' ? 'Driver' : 'None'}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-bodydark">
                        <strong>Note:</strong>
                        {orderDetail.tip_payment_method === 'credit_card'
                          ? ' This tip was paid by the customer during checkout with their credit card.'
                          : orderDetail.tip_payment_method === 'cash'
                          ? ' This tip was collected in cash by the delivery driver.'
                          : ' No tip information available.'}
                      </div>
                    </div>
                  </div>
                )}

                {/* Driver Selection Modal */}
                {showDriverModal && (
                  <div className="fixed inset-0 z-[999999] overflow-y-auto bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-fadeIn">
                      {/* Modal Header */}
                      <div className="bg-gradient-to-r from-primary to-secondary p-4 flex justify-between items-center">
                        <h3 className="font-medium text-white text-lg flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                          </svg>
                          Select Driver
                        </h3>
                        <button
                          onClick={() => setShowDriverModal(false)}
                          className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors duration-200 text-white"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      {/* Modal Content */}
                      <div className="p-0 flex flex-col h-[calc(90vh-64px)]">
                        {/* Map Section */}
                        <div className="h-[50%] relative">
                          <MapContainer
                            center={
                              orderDetail.store &&
                              orderDetail.store.location_x != null &&
                              orderDetail.store.location_y != null
                                ? [
                                    orderDetail.store.location_x,
                                    orderDetail.store.location_y,
                                  ]
                                : [0, 0]
                            }
                            zoom={6}
                            style={{ height: "100%", width: "100%" }}
                            className="z-10"
                          >
                            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                            {/* Drivers markers */}
                            {drivers &&
                              drivers
                                .filter(
                                  (driver) =>
                                    driver.location_x != null &&
                                    driver.location_y != null
                                )
                                .map((driver) => {
                                  console.log("Rendering driver marker:", driver.id, driver.full_name, [driver.location_x, driver.location_y]);
                                  return (
                                  <Marker
                                    key={driver.id}
                                    icon={driverIcon}
                                    position={[
                                      driver.location_x,
                                      driver.location_y,
                                    ]}
                                    eventHandlers={{
                                      click: () => {
                                        setSelectedDriverInModal(driver);
                                      },
                                    }}
                                  >
                                    <Popup className="custom-popup">
                                      <div className="p-1">
                                        <div className="font-semibold text-black text-base mb-1 flex items-center">
                                          <span className="bg-primary/10 text-primary p-1.5 rounded-md mr-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                            </svg>
                                          </span>
                                          {driver.full_name}
                                        </div>
                                        <div className="pl-9 space-y-1 text-sm">
                                          <div className="flex items-center text-bodydark">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                                            </svg>
                                            {driver.phone || "No phone"}
                                          </div>
                                          {driver.city && (
                                            <div className="flex items-center text-bodydark">
                                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                                              </svg>
                                              {driver.city}
                                            </div>
                                          )}
                                        </div>

                                      </div>
                                    </Popup>
                                  </Marker>
                                );
                              })}

                            {/* Store marker */}
                            {orderDetail.store &&
                              orderDetail.store.location_x != null &&
                              orderDetail.store.location_y != null && (
                                <Marker
                                  position={[
                                    orderDetail.store.location_x,
                                    orderDetail.store.location_y,
                                  ]}
                                  icon={storeIcon}
                                >
                                  <Popup className="custom-popup">
                                    <div className="p-1">
                                      <div className="font-semibold text-black text-base mb-1 flex items-center">
                                        <span className="bg-warning/10 text-warning p-1.5 rounded-md mr-2">
                                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" />
                                          </svg>
                                        </span>
                                        Restaurant
                                      </div>
                                      <div className="pl-9 space-y-1 text-sm">
                                        <div className="font-medium text-black">
                                          {orderDetail.store.store_name ?? ""}
                                        </div>
                                        {orderDetail.store.address && (
                                          <div className="flex items-center text-bodydark">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                                            </svg>
                                            {orderDetail.store.address}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </Popup>
                                </Marker>
                              )}

                            {/* Client marker */}
                            {orderDetail.delivery_type === "DELIVERY" &&
                              orderDetail.location_x != null &&
                              orderDetail.location_y != null && (
                                <Marker
                                  icon={clientIcon}
                                  position={[
                                    orderDetail.location_x,
                                    orderDetail.location_y,
                                  ]}
                                >
                                  <Popup className="custom-popup">
                                    <div className="p-1">
                                      <div className="font-semibold text-black text-base mb-1 flex items-center">
                                        <span className="bg-success/10 text-success p-1.5 rounded-md mr-2">
                                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                          </svg>
                                        </span>
                                        Customer
                                      </div>
                                      <div className="pl-9 space-y-1 text-sm">
                                        <div className="font-medium text-black">
                                          {orderDetail.first_name} {orderDetail.last_name}
                                        </div>
                                        <div className="flex items-center text-bodydark">
                                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                                          </svg>
                                          {orderDetail.phone || "No phone"}
                                        </div>
                                        <div className="flex items-center text-bodydark">
                                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                                          </svg>
                                          Delivery Address
                                        </div>
                                      </div>
                                    </div>
                                  </Popup>
                                </Marker>
                              )}
                          </MapContainer>
                        </div>

                        {/* Information Section */}
                        <div className="flex flex-1 overflow-hidden">
                          {/* Drivers List */}
                          <div className="w-1/2 border-r border-stroke overflow-y-auto p-4">
                            <h4 className="text-lg font-semibold text-black mb-4">Available Drivers</h4>
                            <div className="space-y-3">
                              {drivers?.map((driver) => (
                                <div
                                  key={driver.id}
                                  onClick={() => setSelectedDriverInModal(driver)}
                                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                                    selectedDriverInModal?.id === driver.id
                                      ? "border-primary bg-primary/5"
                                      : driver.is_online
                                        ? "border-green-500 bg-green-50 hover:bg-green-100"
                                        : "border-red-300 bg-red-50 hover:bg-red-100"
                                  }`}
                                >
                                  <div className="flex items-center">
                                    <div className={`p-2.5 rounded-lg mr-3 ${driver.is_online ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                      </svg>
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex justify-between items-center">
                                        <div className="font-medium text-black">{driver.full_name}</div>
                                        <span className={`text-xs px-2 py-1 rounded-full ${driver.is_online ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
                                          {driver.is_online ? "Online" : "Offline"}
                                        </span>
                                      </div>
                                      <div className="text-xs text-bodydark">{driver.phone}</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Selected Driver Details */}
                          <div className="w-1/2 p-4 overflow-y-auto">
                            {selectedDriverInModal ? (
                              <div>
                                <h4 className="text-lg font-semibold text-black mb-4">Driver Details</h4>
                                <div className="space-y-4">
                                  <div className="flex items-center mb-4 pb-4 border-b border-stroke">
                                    <div className="bg-primary/10 p-2.5 rounded-lg text-primary mr-3.5">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                      </svg>
                                    </div>
                                    <div>
                                      <div className="text-xs font-medium text-bodydark uppercase">
                                        Driver Name
                                      </div>
                                      <div className="text-base font-medium text-black">
                                        {selectedDriverInModal.full_name ?? "---"}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="space-y-3">
                                    <div className="flex items-center p-3 bg-gray-1 rounded-lg">
                                      <div className="text-xs font-medium text-bodydark uppercase w-20">
                                        Status:
                                      </div>
                                      <div className={`font-medium text-sm px-3 py-1 rounded-full ${selectedDriverInModal.is_online ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
                                        {selectedDriverInModal.is_online ? "Online" : "Offline"}
                                      </div>
                                    </div>
                                    <div className="flex items-center p-3 bg-gray-1 rounded-lg">
                                      <div className="text-xs font-medium text-bodydark uppercase w-20">
                                        Email:
                                      </div>
                                      <div className="font-medium text-black text-sm break-all">
                                        {selectedDriverInModal.email ?? "---"}
                                      </div>
                                    </div>

                                    <div className="flex items-center p-3 bg-gray-1 rounded-lg">
                                      <div className="text-xs font-medium text-bodydark uppercase w-20">
                                        Phone:
                                      </div>
                                      <div className="font-medium text-black text-sm">
                                        {selectedDriverInModal.phone ?? "---"}
                                      </div>
                                    </div>

                                    <div className="flex items-center p-3 bg-gray-1 rounded-lg">
                                      <div className="text-xs font-medium text-bodydark uppercase w-20">
                                        City:
                                      </div>
                                      <div className="font-medium text-black text-sm">
                                        {selectedDriverInModal.city ?? "---"}
                                      </div>
                                    </div>
                                  </div>

                                  {!selectedDriverInModal.is_online && (
                                    <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg mb-4">
                                      <div className="flex items-start">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                                        </svg>
                                        <div>
                                          <p className="font-medium">Warning: Driver is offline</p>
                                          <p className="text-xs mt-1">This driver is currently offline and may not be available to accept orders.</p>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  <button
                                    onClick={() => {
                                      if (!selectedDriverInModal.is_online) {
                                        // Show confirmation for offline driver
                                        if (!window.confirm("This driver is currently offline. Are you sure you want to assign this order to them?")) {
                                          return;
                                        }
                                      }
                                      setDriverSelect(selectedDriverInModal.id);
                                      setShowDriverModal(false);
                                      setSelectedDriverInModal(null);
                                    }}
                                    className={`w-full py-2.5 text-sm font-medium text-white rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md flex items-center justify-center mt-4 ${
                                      selectedDriverInModal.is_online
                                        ? "bg-primary hover:bg-primary/90"
                                        : "bg-orange-500 hover:bg-orange-600"
                                    }`}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                                    </svg>
                                    {selectedDriverInModal.is_online ? "Assign This Driver" : "Assign Anyway (Offline)"}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center h-full text-center">
                                <div className="bg-primary/10 p-4 rounded-full text-primary mb-4">
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                                  </svg>
                                </div>
                                <h4 className="text-lg font-semibold text-black mb-2">No Driver Selected</h4>
                                <p className="text-bodydark text-sm max-w-xs">
                                  Select a driver from the list or click on a driver marker on the map to view details
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Location Tracking Modal */}
                {showLocationModal && (
                  <div className="fixed inset-0 z-[999999] overflow-y-auto bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden animate-fadeIn">
                      {/* Modal Header */}
                      <div className="bg-gradient-to-r from-primary to-secondary p-4 flex justify-between items-center">
                        <h3 className="font-medium text-white text-lg flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                          </svg>
                          Location Tracking
                        </h3>
                        <div className="flex items-center gap-3">
                          <div className="text-xs font-medium text-white bg-white/10 px-2.5 py-1.5 rounded-lg border border-white/20">
                            Auto-refresh: 50s
                          </div>
                          <button
                            onClick={() => {
                              setShowLocationModal(false);
                              if (locationRefreshInterval) {
                                clearInterval(locationRefreshInterval);
                                setLocationRefreshInterval(null);
                              }
                            }}
                            className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors duration-200 text-white"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Modal Content */}
                      <div className="p-0 h-[70vh]">
                        <MapContainer
                          center={
                            orderDetail.store &&
                            orderDetail.store.location_x != null &&
                            orderDetail.store.location_y != null
                              ? [
                                  orderDetail.store.location_x,
                                  orderDetail.store.location_y,
                                ]
                              : [0, 0]
                          }
                          zoom={6}
                          style={{ height: "100%", width: "100%" }}
                          className="z-10"
                        >
                          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                          {/* Drivers markers */}
                          {
                            orderDetail.delivery &&  orderDetail.delivery.location_x != null &&
                            orderDetail.delivery.location_y != null && (
                              <Marker
                                  key={orderDetail.delivery.id}
                                  icon={driverIcon}
                                  position={[
                                    orderDetail.delivery.location_x,
                                    orderDetail.delivery.location_y,
                                  ]}
                                >
                                  <Popup className="custom-popup">
                                    <div className="p-1">
                                      <div className="font-semibold text-black text-base mb-1 flex items-center">
                                        <span className="bg-primary/10 text-primary p-1.5 rounded-md mr-2">
                                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                          </svg>
                                        </span>
                                        {orderDetail.delivery.full_name}
                                      </div>
                                      <div className="pl-9 space-y-1 text-sm">
                                        <div className="flex items-center text-bodydark">
                                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                                          </svg>
                                          {orderDetail.delivery.phone || "No phone"}
                                        </div>
                                        {orderDetail.delivery.city && (
                                          <div className="flex items-center text-bodydark">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                                            </svg>
                                            {orderDetail.delivery.city}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </Popup>
                                </Marker>
                            )
                          }


                          {/* Store marker */}
                          {orderDetail.store &&
                            orderDetail.store.location_x != null &&
                            orderDetail.store.location_y != null && (
                              <Marker
                                position={[
                                  orderDetail.store.location_x,
                                  orderDetail.store.location_y,
                                ]}
                                icon={storeIcon}
                              >
                                <Popup className="custom-popup">
                                  <div className="p-1">
                                    <div className="font-semibold text-black text-base mb-1 flex items-center">
                                      <span className="bg-warning/10 text-warning p-1.5 rounded-md mr-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z" />
                                        </svg>
                                      </span>
                                      Restaurant
                                    </div>
                                    <div className="pl-9 space-y-1 text-sm">
                                      <div className="font-medium text-black">
                                        {orderDetail.store.store_name ?? ""}
                                      </div>
                                      {orderDetail.store.address && (
                                        <div className="flex items-center text-bodydark">
                                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                                          </svg>
                                          {orderDetail.store.address}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </Popup>
                              </Marker>
                            )}

                          {/* Client marker */}
                          {orderDetail.delivery_type === "DELIVERY" &&
                            orderDetail.location_x != null &&
                            orderDetail.location_y != null && (
                              <Marker
                                icon={clientIcon}
                                position={[
                                  orderDetail.location_x,
                                  orderDetail.location_y,
                                ]}
                              >
                                <Popup className="custom-popup">
                                  <div className="p-1">
                                    <div className="font-semibold text-black text-base mb-1 flex items-center">
                                      <span className="bg-success/10 text-success p-1.5 rounded-md mr-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                        </svg>
                                      </span>
                                      Customer
                                    </div>
                                    <div className="pl-9 space-y-1 text-sm">
                                      <div className="font-medium text-black">
                                        {orderDetail.first_name} {orderDetail.last_name}
                                      </div>
                                      <div className="flex items-center text-bodydark">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
                                        </svg>
                                        {orderDetail.phone || "No phone"}
                                      </div>
                                      <div className="flex items-center text-bodydark">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1.5">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
                                        </svg>
                                        Delivery Address
                                      </div>
                                    </div>
                                  </div>
                                </Popup>
                              </Marker>
                            )}
                        </MapContainer>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        ) : null}
      </div>
    </DefaultLayout>
  );
}

export default OrderDetailScreen;
